
































































import { cloneDeep } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import { ALForm } from '@/common/component.types';
import {
  IDocumentData,
  IQuoteDocumentData,
  IQuoteProductDocument,
  IQuoteProductDocumentData,
  QuoteProductDocument
} from '@/models';
import { FieldErrors, SystemError } from '@/models/error/error';
import { ISystemError, ISystemErrorData } from '@/models/error/error.types';

import mixins from '@/utils/mixins';
import authMixin from '@/mixins/auth';
import formRulesMixin from '@/mixins/file-upload-rules';

import ALDialog from '../core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DragDropFileInput from '../common/DragDropFileInput.vue';

const baseMixins = mixins(authMixin, formRulesMixin);

export interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: ALForm;
  };
}

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'DocumentDetailFormDialog',
    components: {
      ALDialog,
      DragDropFileInput,
      ErrorAlert
    },

    data() {
      return {
        dialog: false,
        loading: false,

        isFormValid: false,

        documentData: {
          quote_document: {
            document: {} as IDocumentData
          } as IQuoteDocumentData
        } as IQuoteProductDocumentData,

        error: null as ISystemError | null
      };
    },

    computed: {
      ...mapGetters({
        getQuoteProductDocumentById: 'document/getQuoteProductDocumentById'
      })
    },

    methods: {
      ...mapActions({
        createNewFileRevision: 'document/updateQuoteProductDocument'
      }),

      resetFormValidation(): void {
        if (this.$refs.form) {
          return this.$refs.form.resetValidation();
        }
      },

      validateForm(): boolean {
        let valid = false;
        if (this.$refs.form) {
          valid = this.$refs.form.validate();
        }
        return valid;
      },

      open(document: IQuoteProductDocument) {
        this.clearErrors();
        this.resetFormValidation();

        this.documentData = cloneDeep(document);

        this.dialog = true;
      },

      close() {
        this.dialog = false;
      },

      save() {
        this.clearErrors();
        const valid = this.validateForm();

        if (valid) {
          let documentInstance: IQuoteProductDocument | null = null;

          try {
            documentInstance = new QuoteProductDocument(this.documentData);
          } catch (error) {
            if (SystemError.isSystemError(error)) {
              this.error = error;
            }
          }

          if (documentInstance != null) {
            this.loading = true;

            const saveAction = this.createNewFileRevision;

            saveAction(documentInstance)
              .then(() => this.close())
              .catch(error => {
                const systemErrorData: ISystemErrorData = {
                  name: error.name,
                  message: error.message
                };

                if (error.response) {
                  // The request was made and the server responded with a status code
                  // that falls out of the range of 2xx
                  systemErrorData.message = error.response.statusText;
                  if (error.response.data.detail) {
                    systemErrorData.detail = error.response.data.detail;
                  } else if (FieldErrors.isFieldError(error.response.data)) {
                    systemErrorData.fieldErrors = new FieldErrors(
                      error.response.data
                    );
                  }
                } else if (error.request) {
                  // The request was made but no response was received (Can happen on 500 error)
                  systemErrorData.detail =
                    'There was an error in processing your request. Please check with IT Admin';
                }

                this.error = new SystemError(systemErrorData);
              })
              .finally(() => {
                this.loading = false;
              });
          }
        }
      },

      clearErrors() {
        this.error = null;
      }
    }
  });
